import { type TiledLargeMap } from "./useTiledLargeMap";

/**
 * "Paint the drawing into this rectangle" adapter that lets the large map
 * components keep one layout/indicator code path while the tiled and PDF
 * routes coexist. Goes away with the PDF route.
 */
export type DrawingPaint = {
    /** Natural pixel dimensions used for fit-box/aspect math. */
    width: number;
    height: number;
    paint: (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) => void;
};

/** PDF route: paint the decoded bitmap. `undefined` if it was released (detached). */
export function makeBitmapPaint(bitmap: ImageBitmap | undefined): DrawingPaint | undefined {
    if (!bitmap || bitmap.width === 0) {
        return undefined;
    }
    return {
        width: bitmap.width,
        height: bitmap.height,
        paint: (ctx, x, y, width, height) => ctx.drawImage(bitmap, x, y, width, height),
    };
}

/** Tiled route: paint the base preview + visible tiles for the given viewport. */
export function makeTiledPaint(
    tiledMap: Extract<TiledLargeMap, { active: true }>,
    viewportWidth: number,
    viewportHeight: number,
    dpr: number,
): DrawingPaint {
    return {
        width: tiledMap.width,
        height: tiledMap.height,
        paint: (ctx, x, y, width, height) =>
            tiledMap.draw(ctx, { x, y, width, height }, viewportWidth, viewportHeight, dpr),
    };
}
